import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import { startOfMonth, endOfMonth } from 'date-fns';
import { normalizeCalendarAudience } from '@/features/calendar/utils/calendarAudienceUtils';

/**
 * CalendarEventService - CRUD and audience filtering for school calendar events.
 *
 * Events are stored at schools/{schoolId}/calendarEvents/{eventId}.
 * Audience filtering is done client-side after fetching all events for a month range.
 */
class CalendarEventService {
  constructor() {
    this.cache = new Map();       // `${schoolId}-${year}-${month}` -> events
    this.timestamps = new Map();  // same key -> timestamp
    this.ttl = 2 * 60 * 1000;    // 2 minutes
  }

  _cacheKey(schoolId, year, month) {
    return `${schoolId}-${year}-${month}`;
  }

  _collectionRef(schoolId) {
    return collection(db, 'schools', schoolId, 'calendarEvents');
  }

  /**
   * Get events for a month range. Fetches events whose startDate falls in the month,
   * plus events that started before and end during/after the month.
   */
  async getEventsForMonth(schoolId, year, month, forceRefresh = false) {
    if (!schoolId) throw new Error('schoolId is required');

    const key = this._cacheKey(schoolId, year, month);
    const now = Date.now();

    if (!forceRefresh && this.cache.has(key) && (now - this.timestamps.get(key)) < this.ttl) {
      return this.cache.get(key);
    }

    const monthStart = Timestamp.fromDate(startOfMonth(new Date(year, month)));
    const monthEnd = Timestamp.fromDate(endOfMonth(new Date(year, month)));

    try {
      // Query 1: events starting in this month
      const startQ = query(
        this._collectionRef(schoolId),
        where('startDate', '>=', monthStart),
        where('startDate', '<=', monthEnd)
      );

      // Query 2: events that started before this month (filter endDate client-side
      // to avoid composite index requirement on startDate + endDate)
      const spanQ = query(
        this._collectionRef(schoolId),
        where('startDate', '<', monthStart)
      );

      const [startSnap, spanSnap] = await Promise.all([
        getDocs(startQ),
        getDocs(spanQ)
      ]);

      const eventMap = new Map();
      const processDoc = (d) => {
        if (!eventMap.has(d.id)) {
          eventMap.set(d.id, { id: d.id, ...d.data() });
        }
      };

      startSnap.docs.forEach(processDoc);
      // Client-side filter: only keep spanning events that end during/after this month
      spanSnap.docs.forEach(d => {
        const endDate = d.data().endDate;
        if (endDate && endDate.toMillis() >= monthStart.toMillis()) {
          processDoc(d);
        }
      });

      const events = [...eventMap.values()].sort((a, b) => {
        const aTime = a.startDate?.toMillis?.() || 0;
        const bTime = b.startDate?.toMillis?.() || 0;
        return aTime - bTime;
      });

      this.cache.set(key, events);
      this.timestamps.set(key, now);

      return events;
    } catch (error) {
      console.error('[CalendarEventService] Error fetching events:', error);
      throw error;
    }
  }

  /**
   * Create a calendar event.
   */
  async createEvent(schoolId, eventData, userId) {
    if (!schoolId) throw new Error('schoolId is required');
    if (!eventData.title?.trim()) throw new Error('Event title is required');
    if (!eventData.startDate) throw new Error('Start date is required');
    if (!eventData.endDate) throw new Error('End date is required');

    const toTimestamp = (val) => {
      if (val instanceof Timestamp) return val;
      if (val instanceof Date) return Timestamp.fromDate(val);
      return Timestamp.fromDate(new Date(val));
    };

    const doc = {
      title: eventData.title.trim(),
      description: eventData.description?.trim() || '',
      startDate: toTimestamp(eventData.startDate),
      endDate: toTimestamp(eventData.endDate),
      allDay: eventData.allDay || false,
      type: eventData.type || 'school-event',
      color: eventData.color || null,
      audience: normalizeCalendarAudience(eventData.audience),
      importSource: eventData.importSource || null,
      relatedWorkflowInstanceId: eventData.relatedWorkflowInstanceId || null,
      relatedWorkflowStepId: eventData.relatedWorkflowStepId || null,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      schoolId,
    };

    try {
      const ref = await addDoc(this._collectionRef(schoolId), doc);
      this._invalidateCache(schoolId);
      return { id: ref.id, ...doc };
    } catch (error) {
      console.error('[CalendarEventService] Error creating event:', error);
      throw error;
    }
  }

  /**
   * Update a calendar event.
   */
  async updateEvent(schoolId, eventId, updates) {
    if (!schoolId || !eventId) throw new Error('schoolId and eventId are required');

    const toTimestamp = (val) => {
      if (!val) return val;
      if (val instanceof Timestamp) return val;
      if (val instanceof Date) return Timestamp.fromDate(val);
      return Timestamp.fromDate(new Date(val));
    };

    const data = { ...updates, updatedAt: serverTimestamp() };
    if (data.startDate) data.startDate = toTimestamp(data.startDate);
    if (data.endDate) data.endDate = toTimestamp(data.endDate);
    if (data.audience) data.audience = normalizeCalendarAudience(data.audience, { allowLegacy: true });

    try {
      const docRef = doc(db, 'schools', schoolId, 'calendarEvents', eventId);
      await updateDoc(docRef, data);
      this._invalidateCache(schoolId);
      return { id: eventId, ...data };
    } catch (error) {
      console.error('[CalendarEventService] Error updating event:', error);
      throw error;
    }
  }

  /**
   * Delete a calendar event.
   */
  async deleteEvent(schoolId, eventId) {
    if (!schoolId || !eventId) throw new Error('schoolId and eventId are required');

    try {
      const docRef = doc(db, 'schools', schoolId, 'calendarEvents', eventId);
      await deleteDoc(docRef);
      this._invalidateCache(schoolId);
    } catch (error) {
      console.error('[CalendarEventService] Error deleting event:', error);
      throw error;
    }
  }

  /**
   * Batch import events (from .ics import).
   */
  async importEvents(schoolId, events, userId) {
    if (!schoolId) throw new Error('schoolId is required');

    const results = { created: 0, skipped: 0, errors: [] };

    for (const event of events) {
      try {
        await this.createEvent(schoolId, event, userId);
        results.created++;
      } catch (error) {
        results.errors.push({ event: event.title, error: error.message });
        results.skipped++;
      }
    }

    return results;
  }

  /**
   * Filter events by audience for a staff user.
   * Staff see everything by default unless scope is committee-specific
   * and they're not in that committee.
   */
  filterEventsForStaff(events) {
    // Staff see all events (they're part of the school)
    return events;
  }

  _invalidateCache(schoolId) {
    for (const key of this.cache.keys()) {
      if (key.startsWith(schoolId)) {
        this.cache.delete(key);
        this.timestamps.delete(key);
      }
    }
  }
}

export const calendarEventService = new CalendarEventService();
export default calendarEventService;
